// @ts-strict-ignore
import {Component, inject, OnInit, signal} from "@angular/core";
import {NotificationService, SimSessionService} from "@services";
import {ExamSessionStateService} from "../../service/exam-session-state.service";
import {FetchDataType, ParticipantType, SimUser} from "@models";
import {UntilDestroy} from "@ngneat/until-destroy";
import {Translations} from "@translations";
import {Validators} from "@angular/forms";

@UntilDestroy()
@Component({
	selector: "sim-exam-session-step3",
	templateUrl: "./exam-session-step3.component.html",
	styleUrl: "./exam-session-step3.component.scss",
	standalone: false,
})
export class ExamSessionStep3Component implements OnInit {
	// Readonly
	protected readonly T = Translations;
	protected readonly FetchDataType = FetchDataType;
	protected readonly Validators = Validators;
	protected readonly ParticipantType = ParticipantType;

	// Injections
	private _stateService = inject(ExamSessionStateService);
	private _sessionService = inject(SimSessionService);
	private _notificationService = inject(NotificationService);

	// Properties
	public step2Form = this._stateService.step2Form;
	public step3Form = this._stateService.step3Form;

	// Signals
	public selectedStudent = signal<SimUser | null>(null);
	protected readonly excludeIds = this._stateService.excludeUserIds;

	public ngOnInit(): void {
		// Select first student as default
		this.selectedStudent.set(this.step3Form.value.at(0));
	}

	public addStudent(studentUser: SimUser): void {
		this._addToState(studentUser);
		const savePromise = this._sessionService.addParticipantToSession({
			sessionId: this._stateService.session.id,
			participantType: ParticipantType.Learner,
			userId: studentUser.id,
			generateAuthenticationCode: true,
		});
		this._stateService.savePromise = savePromise;
		savePromise.catch(() => {
			this._notificationService.notifyError("Error while adding the student");
			this._removeFromState(studentUser);
		});
	}

	public removeStudent(studentUser: SimUser): void {
		this._removeFromState(studentUser);
		const savePromise = this._sessionService.removeParticipantFromSession({
			sessionId: this._stateService.session.id,
			userId: studentUser.id,
			participantType: ParticipantType.Learner,
		});
		this._stateService.savePromise = savePromise;
		savePromise.catch(() => {
			this._notificationService.notifyError("Error while removing the student");
			this._addToState(studentUser);
		});
	}

	private _addToState(studentUser: SimUser): void {
		const deDupedStudents = new Set([...this.step3Form.value, studentUser]);
		this.step3Form.setValue([...deDupedStudents]);
		this.selectedStudent.set(studentUser);
	}

	private _removeFromState(studentUser: SimUser): void {
		const filteredStudents = this.step3Form.value.filter((student) => student.id !== studentUser.id);
		this.step3Form.setValue([...filteredStudents]);
		if (this.selectedStudent().id === studentUser.id) {
			const newSelectedStudent = filteredStudents.length > 0 ? filteredStudents.at(-1) : null;
			this.selectedStudent.set(newSelectedStudent);
		}
	}
}
